import React from 'react';
import { Document, Page, StyleSheet, View, Text, Image } from '@react-pdf/renderer';
import { DefineStagePDF } from './DefineStagePDF';
import { PackageStagePDF } from './PackageStagePDF';
import { ChooseTablePDF } from './ChooseTablePDF';
import { SpiderChartPDF } from './SpiderChartPDF';
import { OptionDetailsPDF } from './OptionDetailsPDF';
import { PalletDistributionPDF } from './PalletDistributionPDF';
import { EvaluationResultsPDF } from './EvaluationResultsPDF';
import { NewFooterSection } from '@/components/pdf/NewFooterSection';
import { StandardHeader } from '@/components/pdf/StandardHeader';
const styles = StyleSheet.create({
    page: {
        flexDirection: 'column',
        backgroundColor: '#ffffff',
        paddingTop: 20,
        paddingBottom: 80,
        paddingHorizontal: 20,
        fontFamily: 'Helvetica',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20,
        paddingBottom: 10,
        borderBottomWidth: 1,
        borderBottomColor: '#e0e0e0',
    },
    headerLogo: {
        width: 50,
        height: 50,
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: 'bold',
        color: '#1e40af',
    },
    headerSubtitle: {
        fontSize: 12,
        color: '#666',
        marginTop: 2,
    },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 20,
        right: 20,
        textAlign: 'center',
        fontSize: 10,
        color: '#666',
        borderTopWidth: 1,
        borderTopColor: '#e0e0e0',
        paddingTop: 10,
    },
    content: {
        flexGrow: 1,
    },
    confidentialText: {
        fontSize: 8,
        color: '#666',
        textAlign: 'center',
        marginTop: 5,
    },
});

interface ProjectData {
    project_name: string;
    brand_name: string;
    location_name: string;
    customer: string;
    application_name: string;
    primary_package_type: string;
    category: string;
    net_contents: string;
    net_contents_unit: string;
    filled_top_load?: string;
    filled_top_load_unit?: string;
    primary_package_diameter: string;
    primary_package_diameter_unit: string;
    primary_package_height: string;
    primary_package_height_unit: string;
    bottle_design?: string;
    has_tertiary_package: string;
    items_per_case: string;
    pack_per_case?: string;
    pallet_base_type: string;
    secondary_package_type: string;
    secondary_topload_filled?: string;
    secondary_topload_filled_unit?: string;
    secondary_weight: string;
    secondary_weight_unit: string;
    secondary_length: string;
    secondary_length_unit: string;
    secondary_width: string;
    secondary_width_unit: string;
    secondary_height: string;
    secondary_height_unit: string;
    tertiary_material?: string;
    tertiary_bct?: string;
    tertiary_bct_unit?: string;
    tertiary_length?: string;
    tertiary_length_unit?: string;
    tertiary_width?: string;
    tertiary_width_unit?: string;
    tertiary_height?: string;
    tertiary_height_unit?: string;
    tertiary_weight?: string;
    tertiary_weight_unit?: string;
    k_value: string;
}

interface ChooseTableData {
    optionNo: string;
    sourceType?: string;
    palletVariant?: string;
    product: string;
    primary: {
        details: string;
        similarity: number;
    };
    secondary: {
        details: string;
        similarity: number;
    };
    palletSimilarity?: number | null;
    casesPerLayer?: string;
    layersPerPallet?: string;
    pallet_pattern: string;
    transit: string;
    selected: boolean;
}

interface SpiderChartData {
    labels?: string[];
    datasets?: Array<{
        label: string;
        data: number[];
        backgroundColor: string;
        borderColor: string;
    }>;
}

interface OptionData {
    optionNo: string;
    sourceType?: string;
    palletVariant?: string;
    numberOfPallets?: string;
    overallDecision?: string;
    product: string;
    brand?: string;
    category?: string;
    packageMaterial?: string;
    volume?: string;
    filledWeight?: string;
    diameter?: string;
    height?: string;
    bottleDesign?: string;
    itemsCase?: string;
    caseLength?: string;
    caseWidth?: string;
    caseHeight?: string;
    tertiaryPackageType?: string;
    tertiaryLength?: string;
    tertiaryWidth?: string;
    tertiaryHeight?: string;
    palletTypes?: string;
    palletMaterial?: string;
    unitLoadLength?: string;
    unitLoadWidth?: string;
    unitLoadHeight?: string;
    casesPerLayer?: string;
    layersPerPallet?: string;
    itemsPallet?: string;
    palletPatternStack?: string;
    palletImage?: string;
    testResults?: {
        preShipLean?: string;
        postShipLean?: string;
        postShipWalk?: string;
        postStackLean?: string;
        postStackCompressionRebound?: string;
    };
    ftl?: {
        preShip?: { top?: string; middle?: string; bottom?: string };
        postShip?: { top?: string; middle?: string; bottom?: string };
    };
    primaryFiles?: Array<{ url: string; filename: string; folder: string }>;
    secondaryFiles?: Array<{ url: string; filename: string; folder: string }>;
    unitFiles?: Array<{ url: string; filename: string; folder: string }>;
}

interface PalletDistributionData {
    [key: string]: string | number | Array<{ [key: string]: string }> | undefined;
}

interface SelectedCase {
    project_name: string;
    optionData: OptionData | null;
    palletDistributionData: PalletDistributionData;
    evaluationResults?: EvaluationResult[];
}

interface EvaluationResult {
    TransitMode: string;
    Distance: string;
    LinearRegression: {
        Lean: number;
        Walk: number;
        Compression: number;
        SafetyFactor: number;
        OverallRisk: string;
    };
    TreeModel: {
        Lean: number;
        Walk: number;
        Compression: number;
        SafetyFactor: number;
        OverallRisk: string;
    };
}

interface DistributionAnalyticsPDFProps {
    projectData: ProjectData;
    chooseTableData: ChooseTableData[];
    spiderChartData: SpiderChartData;
    spiderChartImageUrl?: string;
    selectedCases: SelectedCase[];
    evaluateResult: EvaluationResult[];
    createdAt: string;
    projectId: string;
}

const Header: React.FC<{ title: string; projectId: string; createdAt: string }> = ({ title, projectId, createdAt }) => {
    const date = new Date(createdAt);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const formattedId = `${year}${month}-DA${projectId}`;

    return (
        <View style={styles.header}>
            <Image src={'/pepsi-main.png'} style={styles.headerLogo} />
            <View style={{ flex: 1, alignItems: 'center' }}>
                <Text style={styles.headerTitle}>Distribution Analytics Report</Text>
                <Text style={styles.headerSubtitle}>{title}</Text>
                <Text style={styles.headerSubtitle}>Project ID: {formattedId}</Text>
            </View>
            <View style={{ width: 80 }} />
        </View>
    );
};

export const DistributionAnalyticsPDF: React.FC<DistributionAnalyticsPDFProps> = ({
    projectData,
    chooseTableData,
    spiderChartData,
    spiderChartImageUrl,
    selectedCases,
    createdAt,
    evaluateResult,
    projectId
}) => {
    const getSelectedCaseTitle = (selectedCase: SelectedCase) => {
        const rawPalletVariant = typeof selectedCase.palletDistributionData?.pallet_variant === 'string'
            ? selectedCase.palletDistributionData.pallet_variant.trim()
            : '';
        const normalizedVariant = rawPalletVariant.toLowerCase();
        const isSpecification = selectedCase.optionData?.sourceType === 'specification'
            || selectedCase.project_name.toUpperCase().startsWith('P');
        const palletVariant = !isSpecification && !['', 'no data', 'specification match'].includes(normalizedVariant)
            ? rawPalletVariant
            : '';

        return [selectedCase.project_name, palletVariant].filter(Boolean).join(' - ');
    };

    const getSelectedCaseKey = (selectedCase: SelectedCase, index: number) => (
        String(selectedCase.palletDistributionData?.id ?? `${selectedCase.project_name}-${selectedCase.palletDistributionData?.pallet_variant || ''}-${index}`)
    );

    return (
        <Document>
            {/* Page 1: Define Stage */}
            <Page size="A4" style={styles.page}>
                <Header title="Project Definition" projectId={projectId} createdAt={createdAt} />
                <View style={styles.content}>
                    <DefineStagePDF data={projectData} />
                    <PackageStagePDF data={projectData} />
                </View>
                <NewFooterSection />
                {/* <Text style={styles.confidentialText}>
                    PRIVILEGED AND CONFIDENTIAL - PROPERTY OF PEPSICO, INC.
                </Text> */}
            </Page>

            {/* Page 2: Package Stage */}
            {/* <Page size="A4" style={styles.page}>
                <StandardHeader isDa={true} title="Package Details" projectId={projectId} createdAt={createdAt} />
                <View style={styles.content}>
                    <PackageStagePDF data={projectData} />
                </View>
                <NewFooterSection />
               
            </Page> */}
            {/* <Text style={styles.confidentialText}>
                    PRIVILEGED AND CONFIDENTIAL - PROPERTY OF PEPSICO, INC.
                </Text> */}
            {/* Page 3: Choose Stage - Table */}
            <Page size="A4" style={styles.page}>
                <StandardHeader isDa={true} title="Similar Cases Analysis - Table" projectId={projectId} createdAt={createdAt} />
                <View style={styles.content}>
                    <ChooseTablePDF data={chooseTableData} />
                </View>
                <NewFooterSection />
                {/* <Text style={styles.confidentialText}>
                    PRIVILEGED AND CONFIDENTIAL - PROPERTY OF PEPSICO, INC.
                </Text> */}
            </Page>

            {/* Page 4: Choose Stage - Spider Chart */}
            <Page size="A4" style={styles.page}>
                <StandardHeader isDa={true} title="Similar Cases Analysis - Comparison Chart" projectId={projectId} createdAt={createdAt} />
                <View style={styles.content}>
                    <SpiderChartPDF data={spiderChartData} chartImageUrl={spiderChartImageUrl} />
                </View>
                <NewFooterSection />
                {/* <Text style={styles.confidentialText}>
                    PRIVILEGED AND CONFIDENTIAL - PROPERTY OF PEPSICO, INC.
                </Text> */}
            </Page>

            {/* Pages for each selected case */}
            {selectedCases.map((selectedCase, index) => {
                const selectedCaseTitle = getSelectedCaseTitle(selectedCase);
                const detailsTitle = selectedCase.optionData?.sourceType === 'physical'
                    ? 'Physical Ship Test Details'
                    : 'Specification Details';
                return (
                    <React.Fragment key={getSelectedCaseKey(selectedCase, index)}>
                        {/* Option Details Page */}
                        <Page size="A4" style={styles.page}>
                            <StandardHeader isDa={true}
                                title={`Case ${selectedCaseTitle} - ${detailsTitle}`}
                                projectId={projectId}
                                createdAt={createdAt}
                            />
                            <View style={styles.content}>
                                <OptionDetailsPDF data={selectedCase.optionData} />
                            </View>
                            <NewFooterSection />
                        </Page>

                        {/* Pallet Distribution Configuration Page */}
                        <Page size="A4" style={styles.page}>
                            <StandardHeader isDa={true}
                                title={`Case ${selectedCaseTitle} - Pallet Configuration & Results`}
                                projectId={projectId}
                                createdAt={createdAt}
                            />
                            <View style={styles.content}>
                                <PalletDistributionPDF data={selectedCase.palletDistributionData} />
                            </View>
                            <NewFooterSection />
                        </Page>
                    </React.Fragment>
                )
            }
            )}
            <Page size="A4" style={styles.page}>
                <StandardHeader isDa={true} title="Risk Assessment" projectId={projectId} createdAt={createdAt} />
                <View style={styles.content}>
                    <EvaluationResultsPDF evaluateResult={evaluateResult} />
                </View>
                <NewFooterSection />
                {/* <Text style={styles.confidentialText}>
                    PRIVILEGED AND CONFIDENTIAL - PROPERTY OF PEPSICO, INC.
                </Text> */}
            </Page>

        </Document>
    );
}; 
